import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import withNavigate from "../withNavigate";
import { Box, Drawer, Button, Tooltip } from "@mui/material";
import CreateDiscount from "./CreateDiscount";
import LinkToCampaignModal from "./LinkToCampaignModal";
import Pagination from "../../../components/commonUI/Pagination/Pagination";
import SearchFilterBar from "../../../components/commonUI/SearchFilterBar/SearchFilterBar";
import DataTable from "../../../components/commonUI/DataTable/DataTable";
import { InfoIcon, LinkIcon } from "../../../components/commonUI/Icons/SvgIcon";


import {
  DISCOUNT_FILTERS,
  STATUS_BADGE_MAP,
  DISCOUNT_COLUMNS,
  MOCK_DISCOUNTS
} from "../../../config/constants";

const DiscountCodesTab = ({
  userDetails,
  navigate,
  selectedDiscounts: propSelectedDiscounts,
  setSelectedDiscounts: propSetSelectedDiscounts,
  discounts: propDiscounts,
  setDiscounts: propSetDiscounts,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentView, setCurrentView] = useState(null);
  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [discountTypeFilter, setDiscountTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [campaignFilter, setCampaignFilter] = useState("any");
  const [pageSize, setPageSize] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("");
  const [internalSelectedDiscounts, setInternalSelectedDiscounts] = useState(
    [],
  );
  const [selectAll, setSelectAll] = useState(false);
  const [internalDiscounts, setInternalDiscounts] = useState(MOCK_DISCOUNTS);
  const [discountToLink, setDiscountToLink] = useState(null);
  const [selectedCampaignForLink, setSelectedCampaignForLink] = useState("");
  const [campaigns, setCampaigns] = useState([
    {
      _id: "camp1",
      name: "Referral",
      status: "Active",
      description: "Referral campaign",
      createdAt: new Date("2026-07-30"),
    },
    {
      _id: "camp2",
      name: "House move campaign",
      status: "Active",
      description: "Campaign for house move services",
      createdAt: new Date("2026-06-15"),
    },
    {
      _id: "camp3",
      name: "Spring promotion",
      status: "Inactive",
      description: "Spring promotional campaign",
      createdAt: new Date("2026-03-01"),
    },
  ]);
  const [campaignDropdownOpen, setCampaignDropdownOpen] = useState(false);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [campaignDropdownPosition, setCampaignDropdownPosition] =
    useState("bottom");
  const campaignDropdownRef = React.useRef(null);

  const selectedDiscounts =
    propSelectedDiscounts !== undefined
      ? propSelectedDiscounts
      : internalSelectedDiscounts;
  const setSelectedDiscounts =
    propSetSelectedDiscounts || setInternalSelectedDiscounts;
  const discounts =
    propDiscounts !== undefined ? propDiscounts : internalDiscounts;
  const setDiscounts = propSetDiscounts || setInternalDiscounts;

  const toggleDrawer = useCallback((open, view = null, discount = null) => {
    setDrawerOpen(open);
    if (open) {
      setCurrentView(view);
      setSelectedDiscount(discount || null);
    }
  }, []);

  const SORT_FIELDS = useMemo(
    () => ({
      code: { asc: "code=1", desc: "code=-1" },
      discountType: { asc: "discountType=1", desc: "discountType=-1" },
      status: { asc: "status=1", desc: "status=-1" },
      uses: { asc: "uses=1", desc: "uses=-1" },
      startDate: { asc: "startDate=1", desc: "startDate=-1" },
      endDate: { asc: "endDate=1", desc: "endDate=-1" },
    }),
    [],
  );

  const sortDiscounts = useCallback(
    (event, field) => {
      if (event) event.preventDefault();
      const currentSort = SORT_FIELDS[field];
      if (!currentSort) return;
      const newSort =
        sort === currentSort.asc ? currentSort.desc : currentSort.asc;
      setSort(newSort);
      setPage(1);
    },
    [sort, SORT_FIELDS],
  );

  const getStatusBadgeClass = useCallback((status) => {
    const statusLower = status?.toLowerCase();
    return STATUS_BADGE_MAP[statusLower] || "badge-secondary";
  }, []);

  const getDiscountTypeBadgeClass = useCallback(
    (discountType) =>
      discountType?.includes("auto-applied")
        ? "badge-light-warning"
        : "badge-light",
    [],
  );

  const columns = useMemo(() => DISCOUNT_COLUMNS, []);

  const handleReset = useCallback(() => {
    setSearchValue("");
    setDiscountTypeFilter("all");
    setStatusFilter("all");
    setCampaignFilter("any");
    setPage(1);
  }, []);

  const handleSelectDiscount = useCallback(
    (discountId) => {
      setSelectedDiscounts((prev) =>
        prev.includes(discountId)
          ? prev.filter((id) => id !== discountId)
          : [...prev, discountId],
      );
    },
    [setSelectedDiscounts],
  );

  const handleSelectAll = useCallback(() => {
    if (selectAll) {
      setSelectedDiscounts([]);
    } else {
      setSelectedDiscounts(discounts.map((d) => d._id));
    }
    setSelectAll(!selectAll);
  }, [selectAll, setSelectedDiscounts, discounts]);

  const getSortClass = useCallback(
    (key) => {
      if (sort === `${key}=1`) return "sorting-icon sorting-icon-up";
      if (sort === `${key}=-1`) return "sorting-icon sorting-icon-down";
      return "";
    },
    [sort],
  );

  const renderHeader = useCallback(
    () => (
      <>
        <th style={{ width: "40px", textAlign: "center" }}>
          <div className="my-checkbox">
            <input
              type="checkbox"
              id="select-all-checkbox"
              checked={selectAll}
              onChange={handleSelectAll}
            />
            <label htmlFor="select-all-checkbox"></label>
          </div>
        </th>
        {columns.slice(1).map((column) => (
          <th key={column.key}>
            {column.sortable ? (
              <button
                className={getSortClass(column.sortKey)}
                onClick={(e) => sortDiscounts(e, column.sortKey)}
              >
                {column.label}
              </button>
            ) : (
              column.label
            )}
          </th>
        ))}
      </>
    ),
    [columns, selectAll, getSortClass, sortDiscounts, handleSelectAll],
  );

  const handleEditClick = useCallback(
    (discount) => {
      toggleDrawer(true, "edit", discount);
    },
    [toggleDrawer],
  );

  const handleLinkCampaignClick = useCallback(
    (discount) => {
      setDiscountToLink(discount);
      setSelectedCampaignForLink("");
      toggleDrawer(true, "link", discount);
    },
    [toggleDrawer],
  );

  const renderDiscountRow = useCallback(
    (discount, index) => {
      const isSelected = selectedDiscounts.includes(discount._id);
      const hasAssignedTo = !!discount.assignedTo;
      const hasCampaign = discount.campaign && discount.campaign !== "—";
      const hasStartTime = discount.startTime && discount.startTime !== "—";
      const hasEndTime = discount.endTime && discount.endTime !== "—";

      return (
        <tr key={index}>
          <td className="mb-16">
            <div className="my-checkbox">
              <input
                type="checkbox"
                id={`checkbox-${discount._id}`}
                name="my-checkbox"
                checked={isSelected}
                onChange={() => handleSelectDiscount(discount._id)}
              />
              <label htmlFor={`checkbox-${discount._id}`}></label>
            </div>
          </td>
          <td>
            {discount.code}
            <br />
            <span className="text-muted">{discount.description}</span>
            {hasAssignedTo && (
              <>
                <br />
                <span className="text-muted">{discount.assignedTo}</span>
              </>
            )}
          </td>
          <td>
            <div className="discount-type-column">
              <div className="discount-badge-wrapper">
                <span
                  className={`badge ${getDiscountTypeBadgeClass(discount.discountType)}`}
                >
                  {discount.discountType}
                  <Tooltip
                    title="Assigned to one Client, shown in their account, and auto-applied at checkout."
                    arrow
                    placement="top"
                    componentsProps={{
                      tooltip: {
                        sx: {
                          backgroundColor: "#ffffff !important",
                          color: "#000000",
                          fontSize: "14px",
                          padding: "16px 20px",
                          border: "1px solid #e5e7eb",
                          borderRadius: "6px",
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                          minWidth: "280px",
                        },
                      },
                      arrow: {
                        sx: {
                          color: "#ffffff !important",
                          "&::before": {
                            border: "1px solid #e5e7eb",
                          },
                        },
                      },
                    }}
                  >
                    <span className="discount-info-icon">
                      <InfoIcon width="14" height="14" />
                    </span>
                  </Tooltip>
                </span>
              </div>
              {hasCampaign && (
                <div className="campaign-link-text">
                  <LinkIcon
                    style={{ marginRight: "6px", display: "inline" }}
                    width="14"
                    height="14"
                  />
                  {discount.campaign}
                </div>
              )}
            </div>
          </td>
          <td>
            <span className={`badge ${getStatusBadgeClass(discount.status)}`}>
              {discount.status}
            </span>
          </td>
          <td className="text-center">{discount.uses}</td>
          <td>
            {discount.startDate}
            {hasStartTime && (
              <>
                <br />
                <span className="text-muted">{discount.startTime}</span>
              </>
            )}
          </td>
          <td>
            {discount.endDate}
            {hasEndTime && (
              <>
                <br />
                <span className="text-muted">{discount.endTime}</span>
              </>
            )}
          </td>
          <td>
            <Button
              className="btn btn-sm"
              onClick={() => handleEditClick(discount)}
              sx={{
                backgroundColor: "#000",
                color: "#fff",
                padding: "6px 16px",
                fontSize: "14px",
                fontWeight: "500",
                border: "none",
                marginRight: "8px",
                "&:hover": { backgroundColor: "#1a1a1a" },
              }}
            >
              Edit
            </Button>
            <Button
              className="btn btn-sm"
              onClick={() => handleLinkCampaignClick(discount)}
              sx={{
                backgroundColor: "#17a2b8",
                color: "#fff",
                padding: "6px 16px",
                fontSize: "14px",
                fontWeight: "500",
                border: "none",
                "&:hover": { backgroundColor: "#138496" },
              }}
            >
              Assign Campaign
            </Button>
          </td>
        </tr>
      );
    },
    [
      selectedDiscounts,
      getDiscountTypeBadgeClass,
      getStatusBadgeClass,
      handleSelectDiscount,
      handleEditClick,
      handleLinkCampaignClick,
    ],
  );

  return (
    <>
      <SearchFilterBar
        searchValue={searchValue}
        onSearchChange={(value) => setSearchValue(value)}
        onSearchSubmit={() => {}}
        searchPlaceholder="Search discounts or users..."
        onReset={handleReset}
        filters={[
          {
            type: "select",
            key: "discountType",
            label: "Discount type",
            value: discountTypeFilter,
            onChange: setDiscountTypeFilter,
            placeholder: "All discount types",
            options: DISCOUNT_FILTERS.type,
          },
          {
            type: "select",
            key: "status",
            label: "Status",
            value: statusFilter,
            onChange: setStatusFilter,
            placeholder: "All",
            options: DISCOUNT_FILTERS.status,
          },
          {
            type: "select",
            key: "campaign",
            label: "Campaign",
            value: campaignFilter,
            onChange: setCampaignFilter,
            placeholder: "Any campaign",
            options: DISCOUNT_FILTERS.campaign,
          },
        ]}
      />

      <DataTable
        data={discounts}
        columns={columns}
        renderRow={renderDiscountRow}
        onSort={sortDiscounts}
        getSortClass={getSortClass}
        renderHeader={renderHeader}
        emptyMessage="No Discount Codes found"
        tableClassName="table clientTable"
        colSpan={8}
      />

      <Pagination
        pageSize={pageSize}
        totalPages={totalPages}
        page={page}
        setPage={setPage}
        inputValue={page}
        setInputValue={setPage}
        setPageSize={setPageSize}
      />

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => toggleDrawer(false)}
        ModalProps={{
          BackdropProps: {
            style: { backgroundColor: "rgba(0, 0, 0, 0.8)" },
          },
        }}
        sx={{
          boxShadow: "none",
          "& .MuiDrawer-paper": { boxShadow: "none" },
        }}
      >
        <Box
          sx={{ width: 600 }}
          role="presentation"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          {currentView === "link" ? (
            <LinkToCampaignModal
              discount={discountToLink}
              campaigns={campaigns}
              selectedCampaign={selectedCampaignForLink}
              onSelectCampaign={setSelectedCampaignForLink}
              onClose={() => toggleDrawer(false)}
              campaignDropdownOpen={campaignDropdownOpen}
              setCampaignDropdownOpen={setCampaignDropdownOpen}
              campaignDropdownRef={campaignDropdownRef}
              campaignDropdownPosition={campaignDropdownPosition}
              setCampaignDropdownPosition={setCampaignDropdownPosition}
              campaignsLoading={campaignsLoading}
              userDetails={userDetails}
              onCampaignsChange={setCampaigns}
            />
          ) : (
            <CreateDiscount
              view={currentView}
              discount={selectedDiscount}
              onClose={() => toggleDrawer(false)}
            />
          )}
        </Box>
      </Drawer>
    </>
  );
};

DiscountCodesTab.propTypes = {
  userDetails: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
};

export default withNavigate(DiscountCodesTab);
